/**
 * Allows letters, digits, spaces, and common punctuation marks.
 * Prevents emoji and other special symbols.
 */
export const generalTextPattern = /^[\w\s.,!?()\-'"@#$%&*+=:;[\]{}|<>/`~]*$/;

/**
 * Allows only alphabets and spaces.
 */
export const onlyLettersAndSpaces = /^[A-Za-z ]+$/;

/**
 * Regex pattern to validate Pakistani mobile phone numbers.
 *
 * ✅ Valid formats:
 *   - 03001234567
 *   - +923001234567
 *   - 923001234567

 */
export const pakistanCellNumberPattern = /^(?:\+92|92|0)3[0-9]{9}$/;

/**
 * Phone number validation pattern for all forms
 * Supports both Pakistani mobile numbers and international formats
 * Enforces valid length (10-13 digits depending on country code)
 * Accepts formats:
 *   - 03XXXXXXXXX (local - 11 digits)
 *   - +92XXXXXXXXXX (international - 13 digits)
 *   - +92 03XXXXXXXX (international with space)
 *   - International numbers (+X to +XXXXXXXXXXXXX)
 */
export const phoneNumberPattern = /^(03\d{9}|\+923\d{9})$/;

// NTN/FTN validation patterns for Pakistani tax numbers
export const ntnFtnPattern = /^\d{7}(-\d{1})?$|^\d{9}$/;
// This pattern allows:
// - 7 digits (e.g., 1234567)
// - 7 digits with optional check digit (e.g., 1234567-8)
// - 9 digits (e.g., 123456789)

export const pakistanCNICPattern = /^(\d{5}-\d{7}-\d{1}|\d{13})$/;

/**
 * Validates date of birth with minimum age requirement
 * @param dobValue - The date string to validate
 * @param minAge - Minimum age required (default: 15)
 * @returns boolean indicating if the date is valid and meets age requirement
 */
export const validateDateOfBirth = (dobValue: string, minAge: number = 15): boolean => {
	if (!dobValue) return false;
	
	const selectedDate = new Date(dobValue);
	const today = new Date();
	const minDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
	
	// Check if date is valid and meets minimum age requirement
	return selectedDate <= minDate && !isNaN(selectedDate.getTime());
};

/**
 * Comprehensive email validation pattern
 * 
 * ✅ Valid formats:
 *   - user@example.com
 *   - user.name@example.com
 *   - user+tag@example.co.uk
 *   - user123@subdomain.example.com
 * 
 * ❌ Invalid formats:
 *   - @example.com (missing local part)
 *   - user@ (missing domain)
 *   - user@.com (invalid domain)
 *   - user@example (missing TLD)
 *   - user..name@example.com (consecutive dots)
 *   - user@example..com (consecutive dots in domain)
 *   - user@example.comextra (extra characters after TLD)
 */
export const emailPattern = /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/**
 * Enhanced email validation with additional checks
 * - Must start and end with alphanumeric character
 * - Allows dots, hyphens, underscores in local part
 * - Domain must have valid TLD (at least 2 characters) and must END with TLD
 * - No consecutive special characters
 * - No extra characters after TLD (email must end with TLD)
 */
export const strictEmailPattern = /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/**
 * Pakistani landline number pattern
 * ✅ Valid formats:
 *   - 021-1234567 (Karachi)
 *   - 0211234567 (without hyphen)
 *   - 042-1234567 (Lahore)
 *   - 051-1234567 (Islamabad)
 *   - +92-21-1234567 (international format)
 *   - +922112345678 (international without hyphens)
 * ❌ Invalid (mobile numbers - start with 03):
 *   - 03001234567
 *   - +923001234567
 * 
 * Landline area codes: 02x, 04x, 05x, 06x, 07x, 08x, 09x (NOT 03x which is mobile)
 */
export const pakistanLandlineNumberPattern = /^(?:(?:\+92[-]?)?(?:0?[24-9][0-9]{1,2}[-]?[0-9]{6,8})|(?:0[24-9][0-9][-]?[0-9]{6,8}))$/;